import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useIntl } from "react-intl";
import NumericTextField from "components/NumericTextField";
import { ISubjectAreaOption } from "services/KnowfullyAdminClient";

// One credit row: a Subject Area and the credit it is worth. The Total the block shows is
// informational, no rule requires the rows to add up to the qualification's own Credits.
export interface CreditLine {
  // React key. Rows loaded from the server carry their persisted id here; rows the author
  // just added carry a locally generated one. Never the array index: removing a row from
  // the middle shifts every index below it and the inputs reconcile onto the wrong row.
  key: string;
  // Persisted SkuQualificationSubjectArea id, absent while the row is new.
  id?: string;
  subjectAreaId?: string;
  credits: number;
}

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 2,
    mb: 1,
  },
  // The accreditor name runs long, so it hangs off the right edge instead of pushing
  // the label into it.
  headerAccreditor: { textAlign: "right", flex: 1 },
  row: {
    display: "flex",
    gap: 2,
    alignItems: "flex-start",
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 1.5,
    mb: 1,
  },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center" },
} as const;

let nextLineKey = 0;

export const newCreditLine = (): CreditLine => ({
  key: `new-${(nextLineKey += 1)}`,
  credits: 0,
});

// The block always shows a row to fill instead of a bare ADD CREDIT. Showing it does not
// touch the form value, so a save that never reached this row still leaves the stored
// splits alone.
const emptyRow: CreditLine = { key: "empty", credits: 0 };

type CreditsSlotProps = {
  value: CreditLine[];
  onChange: (lines: CreditLine[]) => void;
  // Shown when every entry in the wizard shares one Accreditation Body, so the author knows
  // whose subject area names these are.
  accreditorLabel?: string;
  subjectAreaOptions?: ISubjectAreaOption[];
  disabled?: boolean;
  // The Sku's own credits: the qualification's total until it has credit lines of its own.
  skuCredits?: number;
};

export default function CreditsSlot({
  value,
  onChange,
  accreditorLabel,
  subjectAreaOptions = [],
  disabled = false,
  skuCredits,
}: CreditsSlotProps) {
  const { formatMessage: t } = useIntl();
  const lines = value.length > 0 ? value : [emptyRow];
  const fromSku = value.length === 0 && skuCredits != null;
  const total = fromSku
    ? skuCredits
    : lines.reduce((sum, line) => sum + (Number(line.credits) || 0), 0);
  const totalText = (
    <Typography variant="caption" color="text.secondary" tabIndex={fromSku ? 0 : undefined}>
      {t({ id: "totalCredits" }, { total: <strong key="t">{total.toFixed(2)}</strong> })}
    </Typography>
  );

  const replace = (key: string, patch: Partial<CreditLine>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  // Appending to value, not to lines: the placeholder is not part of the value, so adding onto it
  // would leave two blank rows behind.
  const addLine = () => onChange([...value, newCreditLine()]);
  const removeLine = (key: string) => onChange(lines.filter((line) => line.key !== key));

  return (
    <Box>
      <Box sx={styles.header}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {t({ id: "credits" })}
        </Typography>
        {accreditorLabel && (
          <Typography variant="caption" color="text.secondary" sx={styles.headerAccreditor}>
            {t(
              { id: "subjectAreaAccreditor" },
              {
                accreditor: (
                  <Box component="span" sx={{ color: "text.primary" }} key="acc">
                    {accreditorLabel}
                  </Box>
                ),
              },
            )}
          </Typography>
        )}
      </Box>

      {lines.map((line) => (
        <Box key={line.key} sx={styles.row}>
          <Box sx={{ width: 90 }}>
            <NumericTextField
              variant="standard"
              fullWidth
              disabled={disabled}
              label={t({ id: "amountLabel" })}
              defaultValue={line.credits}
              onChange={(e) => replace(line.key, { credits: Number(e.target.value) })}
            />
          </Box>
          <Autocomplete
            fullWidth
            sx={{ flex: 1 }}
            disabled={disabled}
            options={subjectAreaOptions}
            getOptionLabel={(option) => option.name ?? ""}
            isOptionEqualToValue={(option, selected) =>
              option.subjectAreaId === selected.subjectAreaId
            }
            value={
              subjectAreaOptions.find((option) => option.subjectAreaId === line.subjectAreaId) ??
              null
            }
            onChange={(_, selected) =>
              replace(line.key, { subjectAreaId: selected?.subjectAreaId })
            }
            renderOption={(props, option) => (
              <li {...props} key={option.subjectAreaId}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {/* Only when it adds something: most accreditors label an area exactly as
                      Knowfully names it, and repeating it reads as a duplicate entry. */}
                  {option.accreditorLabel && option.accreditorLabel !== option.name && (
                    <Typography variant="caption" color="text.secondary">
                      {option.accreditorLabel}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={t({ id: "subjectAreaLabel" })}
                placeholder={t({ id: "subjectAreaPlaceholder" })}
              />
            )}
          />
          <IconButton
            size="small"
            disabled={disabled || lines.length <= 1}
            onClick={() => removeLine(line.key)}
            aria-label={t({ id: "removeCreditLine" })}
            sx={{ mt: 2 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box sx={styles.footer}>
        <Button size="small" disabled={disabled} startIcon={<AddIcon />} onClick={addLine}>
          {t({ id: "addCredit" })}
        </Button>
        {fromSku ? (
          <Tooltip title={t({ id: "creditsFromTheSkuDetails" }, { credits: total })}>
            {totalText}
          </Tooltip>
        ) : (
          totalText
        )}
      </Box>
    </Box>
  );
}
